import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ClinicContentBlock } from '../../../data/models/ClinicContentBlock';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { CONSTANTS } from '../../../helper/constants';
import { environment } from '../../../../environments/environment';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-video-block-dialog',
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule],
  templateUrl: './video-block-dialog.component.html',
  styleUrl: './video-block-dialog.component.scss'
})
export class VideoBlockDialogComponent implements OnInit, OnDestroy {
  form!: FormGroup
  isEdit: boolean
  videoFile: File | null = null
  previewFile: File | null = null
  videoFileName: string | null = null
  videoPlaybackUrl: string | null = null
  previewUrl: string | null = null
  videoError: string | null = null
  previewError: string | null = null
  showVideoReplace = false
  private previewBlobUrl: string | null = null
  private videoBlobUrl: string | null = null
  private hasFileChange = false
  private isClosing = false
  private readonly maxVideoSize = 100 * 1024 * 1024
  private readonly maxPreviewSize = 3 * 1024 * 1024

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<VideoBlockDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { block?: ClinicContentBlock }
  ) {
    this.isEdit = !!data?.block
  }

  ngOnInit(): void {
    this.dialogRef.backdropClick().subscribe(() => this.tryClose(null))
    this.dialogRef.keydownEvents().subscribe(e => {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.tryClose(null)
      }
    })

    this.form = this.fb.group({
      title: [this.data?.block?.title || '', [Validators.maxLength(CONSTANTS.maxInput)]],
      body_text: [this.data?.block?.body_text || '', [Validators.maxLength(CONSTANTS.maxInput)]]
    })

    if (this.isEdit && this.data.block?.video_preview_path) {
      const path = this.data.block.video_preview_path
      this.previewUrl = path.startsWith('blob:')
        ? path
        : `${environment.url}/${environment.backendVersion}/clinic/media/${path}`
    }

    if (this.isEdit && this.data.block?.video_path) {
      const path = this.data.block.video_path
      if (path.startsWith('blob:')) {
        this.videoPlaybackUrl = path
      } else {
        this.videoPlaybackUrl = `${environment.url}/${environment.backendVersion}/clinic/media/${path}`
      }
      this.videoFileName = path.startsWith('blob:')
        ? 'Video'
        : path.split('/').pop() || 'Video'
    }
  }

  onVideoSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files?.length) return

    const file = input.files[0]
    this.videoError = null

    if (!['video/mp4', 'video/quicktime'].includes(file.type)) {
      this.videoError = 'Nur MP4 und MOV Dateien sind erlaubt.'
      return
    }

    if (file.size > this.maxVideoSize) {
      this.videoError = CONSTANTS.fileSizeExceededError
      return
    }

    this.videoFile = file
    this.videoFileName = file.name
    this.hasFileChange = true

    if (this.videoBlobUrl) URL.revokeObjectURL(this.videoBlobUrl)
    this.videoBlobUrl = URL.createObjectURL(file)
    this.videoPlaybackUrl = this.videoBlobUrl
    this.showVideoReplace = false
  }

  onPreviewSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files?.length) return

    const file = input.files[0]
    this.previewError = null

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      this.previewError = 'Nur JPG und PNG Dateien sind erlaubt.'
      return
    }

    if (file.size > this.maxPreviewSize) {
      this.previewError = CONSTANTS.fileSizeExceededError
      return
    }

    this.previewFile = file
    this.hasFileChange = true
    if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl)
    this.previewBlobUrl = URL.createObjectURL(file)
    this.previewUrl = this.previewBlobUrl
  }

  downloadVideo(): void {
    if (!this.videoPlaybackUrl) return
    const a = document.createElement('a')
    a.href = this.videoPlaybackUrl
    a.download = this.videoFileName || 'video'
    a.target = '_blank'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  ngOnDestroy(): void {
    if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl)
    if (this.videoBlobUrl) URL.revokeObjectURL(this.videoBlobUrl)
  }

  get isDirty(): boolean {
    return this.form.dirty || this.hasFileChange
  }

  get isValid(): boolean {
    if (this.form.invalid) return false
    if (!this.isEdit && !this.videoFile) return false
    return true
  }

  async tryClose(result: string | null): Promise<void> {
    if (this.isClosing) return
    if (this.isDirty) {
      this.isClosing = true
      try {
        const confirmed = await this.confirmDiscard()
        if (!confirmed) return
      } finally {
        this.isClosing = false
      }
    }
    this.dialogRef.close(result)
  }

  private async confirmDiscard(): Promise<boolean> {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Änderungen verwerfen',
        message: 'Du hast ungespeicherte Änderungen. Wenn du den Dialog verlässt, gehen diese verloren.',
        confirmText: 'Verwerfen'
      }
    })
    return !!(await firstValueFrom(ref.afterClosed()))
  }

  save(): void {
    if (!this.isValid) return
    this.dialogRef.close({
      ...this.form.value,
      file: this.videoFile,
      previewFile: this.previewFile
    })
  }
}
